import { useMutation } from '@apollo/client'
import React, { useState } from 'react'
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import { CREATE_FORM_BUILDER } from '../../../../graphql/mutation'
import { GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID } from '../../../../graphql/query'

interface Props {
  show: boolean
  handleClose: () => void
}

const UploadFormModal: React.FC<Props> = ({ show, handleClose }) => {
  const [selectedFile, setSelectedFile] = useState<any>()
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)
  const [createFormBuilder] = useMutation(CREATE_FORM_BUILDER, {
    refetchQueries: [
      {
        query: GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID,
        variables: {
          input: {
            formUuid: null
          }
        },

        context: {
          headers: {
            'access-control-allow-methods': 'Form-Builder'
          }
        }
      }
    ],
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    }
  })

  const handleFileChange = (event: any) => {
    const file = event.target.files[0]
    processFile(file)
  }

  const processFile = (file: any) => {
    if (file) {
      // CHANGE 1: Added Excel and CSV MIME types to the allowed list.
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', // .xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'text/csv' // .csv
      ]

      if (!allowedTypes.includes(file.type)) {
        // Updated error message to include new file types.
        setUploadError('Please select a PDF, Word, or Excel/CSV file.')
        setSelectedFile(null)
        return
      }

      if (file.size > 10 * 1024 * 1024) {
        setUploadError('File size should be less than 10MB.')
        setSelectedFile(null)
        return
      }

      setSelectedFile(file)
      setUploadError('')
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setUploadError('')
    setUploadSuccess('')
    const fileInput = document.getElementById('file-input') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const handleDragOver = (e: any) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: any) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: any) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files[0]
    processFile(file)
  }

  const removeFileExtension = (filename: string) => {
    return filename.replace(/\.[^/.]+$/, '')
  }

  const getOneYearFromNow = () => {
    const currentDate = new Date()
    const oneYearLater = new Date(currentDate)
    oneYearLater.setFullYear(currentDate.getFullYear() + 1)
    const year = oneYearLater.getFullYear()
    const month = String(oneYearLater.getMonth() + 1).padStart(2, '0')
    const day = String(oneYearLater.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getFileIcon = (fileType: string) => {
    if (fileType === 'application/pdf') {
      return '📄' // PDF
    }
    if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType === 'text/csv') {
      return '📊' // Excel/CSV
    }
    if (fileType.includes('word')) {
      return '📝' // Word
    }
    return '📁' // Generic file
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a document file to upload.')
      return
    }

    setIsUploading(true)
    setUploadError('')
    setUploadSuccess('')

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)

      const response = await fetch('https://demoserver.botgo.io/form-automation/upload-form', {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Upload failed: ${response.status} ${response.statusText}. ${errorText}`)
      }

      const result = await response.json()

      if (result.success && result.form_fields) {
        setUploadSuccess('Document uploaded successfully! Creating form...')
        const formName = removeFileExtension(result.file_info.filename)
        try {
          const createFormResponse = await createFormBuilder({
            variables: {
              input: {
                templateID: 0,
                name: formName,
                formData: result.form_fields,
                description: '',
                expiryDate: getOneYearFromNow(),
                cardBackground: '',
                cardStyle: ''
              }
            }
          })
          console.log('Form created successfully:', createFormResponse)
          setUploadSuccess('Document uploaded and form created successfully!')
          setTimeout(() => {
            setSelectedFile(null)
            setUploadSuccess('')
            handleClose()
          }, 2000)
        } catch (formCreationError) {
          console.error('Form creation error:', formCreationError)
          setUploadError('Document uploaded but failed to create form. Please try again.')
        }
      } else {
        throw new Error('Upload response indicates failure or missing form fields')
      }
    } catch (error) {
      console.error('Upload error:', error)
      if (error instanceof Error) {
        setUploadError(`Failed to upload document: ${error.message}`)
      } else {
        setUploadError('Failed to upload document. Please try again.')
      }
    } finally {
      setIsUploading(false)
    }
  }

  const handleModalClose = () => {
    setSelectedFile(null)
    setUploadError('')
    setUploadSuccess('')
    const fileInput = document.getElementById('file-input') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
    handleClose()
  }

  return (
    <Modal show={show} onHide={handleModalClose} centered size='lg' className='upload-modal'>
      <Modal.Header closeButton className='border-0 pb-2' style={{ padding: '1.5rem 1.5rem 0' }}>
        <Modal.Title className='fs-4 fw-semibold text-dark d-flex align-items-center'>
          <div
            className='me-3 d-flex align-items-center justify-content-center'
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              fontSize: '18px'
            }}
          >
            📄
          </div>
          Upload Document
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className='px-4 pb-4'>
        <p className='text-muted mb-4'>
          Upload a PDF, Word, or Excel document to automatically generate a form with AI-powered
          field extraction.
        </p>

        {uploadError && (
          <Alert variant='danger' className='mb-4 border-0' style={{ borderRadius: '8px' }}>
            {uploadError}
          </Alert>
        )}

        {uploadSuccess && (
          <Alert variant='success' className='mb-4 border-0' style={{ borderRadius: '8px' }}>
            {uploadSuccess}
          </Alert>
        )}

        {/* Upload Area */}
        <div
          className={`upload-dropzone mb-4 ${isDragOver ? 'drag-over' : ''} ${
            selectedFile ? 'file-selected' : ''
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click?.()}
          style={{
            border: '2px dashed #dee2e6',
            borderRadius: '12px',
            padding: '3rem 2rem',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          {!selectedFile ? (
            <>
              <div className='mb-3' style={{ fontSize: '3rem', color: '#6c757d' }}>
                📁
              </div>
              <h5 className='fw-semibold text-dark mb-2'>Drop document here or click to browse</h5>
              <p className='text-muted mb-0 small'>
                Supports PDF, Word, & Excel (xls, xlsx, csv) files up to 10MB
              </p>
            </>
          ) : (
            <>
              <div className='mb-3' style={{ fontSize: '2.5rem', color: '#198754' }}>
                ✓
              </div>
              <h6 className='fw-semibold text-success mb-3'>File Ready</h6>
              <div
                className='selected-file p-3 rounded-3 d-inline-flex align-items-center position-relative'
                style={{
                  backgroundColor: '#d1e7dd',
                  border: '1px solid #badbcc'
                }}
              >
                <div className='me-3' style={{ fontSize: '24px' }}>
                  {getFileIcon(selectedFile.type)}
                </div>
                <div className='text-start me-3'>
                  <div className='fw-medium text-dark small'>{selectedFile.name}</div>
                  <small className='text-muted'>
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </small>
                </div>
                <button
                  type='button'
                  className='btn-close'
                  aria-label='Remove file'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveFile()
                  }}
                  style={{
                    fontSize: '0.7rem',
                    opacity: 0.6
                  }}
                ></button>
              </div>
              <div className='mt-3'>
                <small className='text-muted'>
                  Click the × to remove or drop a new file to replace
                </small>
              </div>
            </>
          )}

          <Form.Control
            id='file-input'
            type='file'
            accept='.pdf,.doc,.docx,.xls,.xlsx,.csv'
            onChange={handleFileChange}
            disabled={isUploading}
            style={{ display: 'none' }}
          />
        </div>
      </Modal.Body>

      <Modal.Footer
        className='border-0 pt-0 justify-content-end'
        style={{ padding: '0 1.5rem 1.5rem' }}
      >
        <Button
          variant='outline-secondary'
          onClick={handleModalClose}
          disabled={isUploading}
          className='me-2'
          style={{ borderRadius: '8px' }}
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          style={{
            borderRadius: '8px',
            minWidth: '120px'
          }}
        >
          {isUploading ? (
            <>
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              ></span>
              Processing...
            </>
          ) : (
            'Generate Form'
          )}
        </Button>
      </Modal.Footer>

      <style>{`
        .upload-dropzone:hover {
          border-color: #6c757d;
          background-color: #f1f3f5;
        }
        
        .upload-dropzone.drag-over {
          border-color: #0d6efd;
          background-color: #e7f1ff;
          transform: scale(1.01);
        }
        
        .upload-dropzone.file-selected {
          border-color: #198754;
          background-color: #d1e7dd;
        }
        
        .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }
        
        .btn-close {
          font-size: 0.9rem;
        }
        
        .btn-close:hover {
          opacity: 1;
        }
      `}</style>
    </Modal>
  )
}

export default UploadFormModal
